import React, { useState, useEffect } from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { PrimaryOutlinedCloseCTAButton } from "../../common/Buttons";
import { Modal, CircularProgress, Typography } from "@material-ui/core";
import CaseService from "../../../services/CaseService";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";

export default function FilterModal({ setModal, modal }) {
  const [caseList, setCaseList] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (modal.state) {
      (async () => {
        try {
          setLoading(true);
          const response = await CaseService.agentCases(
            modal.data.partyId,
            modal.data.id
          );
          if (response?.caseDet) {
            setCaseList(response.caseDet.filter((item) => item.assigned));
          }
        } catch (error) {
          enqueueSnackbar(getErrorMessage(error), { variant: "error" });
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [modal.state, modal.data, enqueueSnackbar]);

  return (
    <StyledModal open={modal.state} onClose={() => setModal({ state: false })}>
      <ModalContainer>
        <Header>
          <Title>Assigned Cases</Title>
          <CloseButton onClick={() => setModal({ state: false })}>
            <img
              src={require("../../../assets/images/closeModal.svg")}
              alt="Close"
            />
          </CloseButton>
        </Header>

        <Body>
          {loading ? (
            <Loader>
              <CircularProgress size={40} thickness={4} />
              <LoaderText>Loading cases...</LoaderText>
            </Loader>
          ) : caseList.length > 0 ? (
            <CaseList>
              {caseList.map((item, index) => (
                <CaseItem key={item.id || index}>
                  <CaseNumber>Case {item.id}</CaseNumber>
                  <CaseTitle>{item.title || "Untitled Case"}</CaseTitle>
                </CaseItem>
              ))}
            </CaseList>
          ) : (
            <EmptyState>
              <EmptyIcon>assignment</EmptyIcon>
              <EmptyTitle>No cases assigned</EmptyTitle>
              <EmptyText>This agent currently has no assigned cases.</EmptyText>
            </EmptyState>
          )}
        </Body>

        <Footer>
          <PrimaryOutlinedCloseCTAButton
            onClick={() => setModal({ state: false })}
            style={{ minWidth: "140px" }}
          >
            Close
          </PrimaryOutlinedCloseCTAButton>
        </Footer>
      </ModalContainer>
    </StyledModal>
  );
}

/* MODERN, CLEAN & PERFECTLY ALIGNED STYLES */

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: modalFadeIn 0.3s ease-out;

  @keyframes modalFadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const Header = styled.div`
  padding: 24px 32px 20px;
  color: white;
  text-align: center;
  position: relative;
`;

const Title = styled.h3`
  margin: 0;
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 20px;
  color: #293461;
`;

const CloseButton = styled.button`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-50%) scale(1.1);
  }

  img {
    width: 16px;
    height: 16px;
    filter: brightness(0) invert(1);
  }
`;

const Body = styled.div`
  padding: 0 32px;
  max-height: 70vh;
  overflow-y: auto;
`;

const Loader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #666;
`;

const LoaderText = styled.div`
  margin-top: 16px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 15px;
  color: #666;
`;

const CaseList = styled.div`
  padding: 16px 0;
`;

const CaseItem = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f8fbfe;
    border-radius: 8px;
    margin: 0 -20px;
    padding: 16px 20px;
  }
`;

const CaseNumber = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 13px;
  color: #00838c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const CaseTitle = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 15px;
  color: ${COLORS.COLOR_DARK};
  line-height: 1.4;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #888;
`;

const EmptyIcon = styled.div`
  font-size: 56px;
  margin-bottom: 16px;
  opacity: 0.3;
`;

const EmptyTitle = styled(Typography)`
  && {
    font-family: ${theme.fonts.primaryFontBold};
    font-size: 18px;
    color: #555;
    margin-bottom: 8px;
  }
`;

const EmptyText = styled.div`
  font-size: 14px;
  color: #777;
`;

const Footer = styled.div`
  padding: 24px 32px;
  background-color: #f8f9fa;
  text-align: center;
  border-top: 1px solid #e0e0e0;
`;
